import { useState } from "react"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import HomeNav from "@/components/home-nav"
import HomeFooter from "@/components/home-footer"

const ROLES = [
  "Project Developer",
  "Investor / Financier",
  "Government / Policy",
  "Consultant / Advisor",
  "Other",
]

export default function WaitlistPage() {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [role, setRole] = useState("")
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState("")

  const validate = () => {
    const next: Record<string, string> = {}
    if (!name.trim()) next.name = "Name is required."
    if (!email.trim()) next.email = "Email is required."
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Enter a valid email address."
    if (!role) next.role = "Please select your role."
    return next
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setServerError("")
    const next = validate()
    if (Object.keys(next).length > 0) {
      setErrors(next)
      return
    }
    setErrors({})
    setIsLoading(true)
    try {
      const url = import.meta.env.VITE_WAITINGLIST_URL as string
      if (!url) throw new Error("Waitlist URL is not configured.")
      await fetch(url, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), role }),
      })
      setSubmitted(true)
    } catch (error) {
      // log error to client
      console.error("Error submitting waitlist form:", error)
      setServerError("Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <HomeNav />
      <div className="flex min-h-[calc(100svh-4rem)]">

        {/* Left — Form panel */}
        <div className="flex flex-1 items-center justify-center bg-white px-6 py-16 sm:px-12">
          <div className="w-full max-w-sm">

            <div className="w-10 h-0.75 bg-secondary mb-8 rounded-full" />

            {submitted ? (
              <div className="space-y-4">
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05]">
                  You're on the list
                </h1>
                <p className="text-[#0D0D0D]/50 text-sm leading-relaxed">
                  Thanks for joining the Kanu Energy waitlist. We'll be in touch at{" "}
                  <span className="text-[#0D0D0D]/70 font-medium">{email}</span> when your access is ready.
                </p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-sm text-[#1D591B] hover:underline font-medium"
                >
                  ← Back to home
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-4xl font-black text-[#0D0D0D] tracking-tight leading-[1.05] mb-2">
                  Join the waitlist
                </h1>
                <p className="text-[#0D0D0D]/50 text-sm leading-relaxed mb-10">
                  Be among the first to access Kanu Energy's renewable energy intelligence platform.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="name"
                      className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60"
                    >
                      Full name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Jane Smith"
                      value={name}
                      onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })) }}
                      disabled={isLoading}
                      aria-invalid={!!errors.name}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 bg-white"
                    />
                    {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="email"
                      className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60"
                    >
                      Email <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })) }}
                      disabled={isLoading}
                      aria-invalid={!!errors.email}
                      className="rounded-none h-11 border-[#0D0D0D]/20 focus-visible:border-[#1D591B] focus-visible:ring-0 bg-white"
                    />
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="role"
                      className="text-xs font-semibold uppercase tracking-widest text-[#0D0D0D]/60"
                    >
                      Role <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={role}
                      onValueChange={(v) => { setRole(v); setErrors((p) => ({ ...p, role: "" })) }}
                      disabled={isLoading}
                    >
                      <SelectTrigger
                        id="role"
                        aria-invalid={errors.role ? "true" : "false"}
                        className="w-full rounded-none h-11 border-[#0D0D0D]/20 focus:border-[#1D591B] focus:ring-0 bg-white text-sm text-[#0D0D0D]"
                      >
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
                  </div>

                  {serverError && <p className="text-xs text-destructive">{serverError}</p>}

                  <Button
                    className="w-full rounded-none bg-primary hover:bg-foreground text-white h-12 font-semibold text-sm tracking-wide transition-colors duration-200 cursor-pointer mt-2"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Joining…" : "Join Waitlist"}
                  </Button>
                </form>
              </>
            )}

          </div>
        </div>

        {/* Right — Brand panel */}
        <div className="hidden lg:flex w-[48%] bg-[url('/kanu_dashboard.png')] bg-contain bg-center bg-no-repeat relative overflow-hidden" />

      </div>
      <HomeFooter />
    </>
  )
}
